import { __awaiter } from "tslib";
import { fetchAIHubEnabled, isCatchUpNativeDisabled, isCreateNativeEnabled, isPrepareNativeModuleEnabled, } from '@office-start/ai-hub-config-service';
import { fetchCapabilities } from '@office-start/capabilities-service';
import { fetchBCAIS, fetchIsCopilotRoute, fetchPinningEligible, } from '@office-start/copilot-route-service';
import { fetchFeatureGates } from '@office-start/feature-gates-service';
import { fetchFeatureSettings } from '@office-start/feature-settings-service';
import { fetchIdentity } from '@office-start/identity-service';
import { fetchLocStrings } from '@office-start/loc-service';
import { getHarmonyAppDefinition } from '@office-start/mos-app-utilities';
import { fetchUserEntitlements } from '@office-start/user-entitlements-service';
import { getCandidateMOS3AppEntries, IMMERSIVEBIZCHAT_APP_ID, ONEDRIVE_APP_ID, PreinstalledAppNames, } from '../constants/preinstalledApps';
export const fetchPreinstalledApps = () => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    const capabilities = yield fetchCapabilities();
    const featureGates = (_a = (yield fetchFeatureGates().catch(() => undefined))) === null || _a === void 0 ? void 0 : _a.result;
    const featureSettings = (_b = (yield fetchFeatureSettings().catch(() => undefined))) === null || _b === void 0 ? void 0 : _b.result;
    const userEntitlements = (_d = (_c = (yield fetchUserEntitlements().catch(() => undefined))) === null || _c === void 0 ? void 0 : _c.result) !== null && _d !== void 0 ? _d : undefined;
    if (!capabilities) {
        throw new Error('Capabilities not found');
    }
    //TODO : Workaround since general localization solution for preinstalled app is not ready yet. It should be replaced later.
    const summarizeActionText = yield getSummarizeActionText();
    const enableAIHub = yield fetchAIHubEnabled();
    const identity = yield fetchIdentity();
    const isCopilotRoute = yield fetchIsCopilotRoute();
    const isBCAIS = yield fetchBCAIS();
    const isPinningEligible = yield fetchPinningEligible();
    return [
        ...(yield getPreinstalledApps(capabilities, featureGates, featureSettings, userEntitlements, summarizeActionText, enableAIHub, identity, isCopilotRoute, isBCAIS, isPinningEligible)),
    ];
});
export const getSummarizeActionText = () => __awaiter(void 0, void 0, void 0, function* () {
    var _e;
    const locStrings = yield fetchLocStrings();
    return (_e = locStrings === null || locStrings === void 0 ? void 0 : locStrings.metaOSHubResources) === null || _e === void 0 ? void 0 : _e.summarizeActionText;
});
export function getFirstPartyAppDefinition(appId) {
    return __awaiter(this, void 0, void 0, function* () {
        const summarizeActionText = yield getSummarizeActionText();
        const candidateMOS3AppEntries = yield getCandidateMOS3AppEntries(summarizeActionText);
        const appEntry = candidateMOS3AppEntries.find((entry) => entry.appId === appId);
        const harmonyApp = getHarmonyAppDefinition(appEntry === null || appEntry === void 0 ? void 0 : appEntry.definition);
        return harmonyApp;
    });
}
export function isFirstPartyApp(appId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (appId === ONEDRIVE_APP_ID ||
            (appId === IMMERSIVEBIZCHAT_APP_ID && (yield isCopilotEnabled()))) {
            return true;
        }
        return false;
    });
}
export const isCopilotEnabled = () => __awaiter(void 0, void 0, void 0, function* () {
    var _f, _g, _h, _j;
    const featureGates = (_f = (yield fetchFeatureGates().catch(() => undefined))) === null || _f === void 0 ? void 0 : _f.result;
    const featureSettings = (_g = (yield fetchFeatureSettings().catch(() => undefined))) === null || _g === void 0 ? void 0 : _g.result;
    const userEntitlements = (_j = (_h = (yield fetchUserEntitlements().catch(() => undefined))) === null || _h === void 0 ? void 0 : _h.result) !== null && _j !== void 0 ? _j : undefined;
    const enableAIHub = yield fetchAIHubEnabled();
    const identity = yield fetchIdentity();
    const isCopilotRoute = yield fetchIsCopilotRoute();
    const isBCAISUser = yield fetchBCAIS();
    const isPinningEligible = yield fetchPinningEligible();
    return isBizChatEnabled(featureGates, featureSettings, userEntitlements, enableAIHub, identity, isCopilotRoute, isBCAISUser, isPinningEligible);
});
//Logic to determine if user has access to the copilot business chat app
export function isBizChatEnabled(featureGates, featureSettings, userEntitlements, enableAIHub, identity, isCopilotRoute, isBCAISUser, isPinningEligible) {
    var _a, _b, _c;
    // early return for MSA users
    if (identity.isMsa) {
        return false;
    }
    // early return if force-install FG is enabled
    if (enableAIHub ||
        ((_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.immersiveBizchatM365AppGates) === null || _a === void 0 ? void 0 : _a.immersiveBizchatM365AppEnabled)) {
        return true;
    }
    const copilotMode = (_b = featureSettings === null || featureSettings === void 0 ? void 0 : featureSettings.copilot) === null || _b === void 0 ? void 0 : _b.mode;
    const isStandaloneApp = (_c = userEntitlements === null || userEntitlements === void 0 ? void 0 : userEntitlements.isStandaloneCopilotApp) !== null && _c !== void 0 ? _c : false;
    // Standalone app always will have copilot experience in ModeB
    if (isStandaloneApp && copilotMode === 'ModeB') {
        return true;
    }
    // Rule0
    if (isBCAISUser) {
        return true;
    }
    // R1
    if (!copilotMode) {
        return false;
    }
    // R2
    if (isCopilotRoute) {
        return true;
    }
    // R3
    if (copilotMode === 'ModeA') {
        return false;
    }
    // R4
    // isPinningEligible
    if (isPinningEligible) {
        return true;
    }
    // R5
    return false;
}
const shouldInstall = (capabilitiesConfig, appEnumName, featureGates, featureSettings, userEntitlements, enableAIHub, identity, isCopilotRoute, isBCAIS, isPinningEligible) => {
    var _a, _b;
    switch (appEnumName) {
        case PreinstalledAppNames.Park:
            return capabilitiesConfig.parkEnabled;
        case PreinstalledAppNames.SpfxTestApp:
        case PreinstalledAppNames.Test:
            return capabilitiesConfig.m365TestAppEnabled;
        case PreinstalledAppNames.Todo:
            return capabilitiesConfig.toDoM365AppEnabled;
        case PreinstalledAppNames.Store:
            return capabilitiesConfig.storeAppEnabled;
        case PreinstalledAppNames.Yammer:
            return capabilitiesConfig.yammerM365AppEnabled;
        case PreinstalledAppNames.ImmersiveBizchat:
            return isBizChatEnabled(featureGates, featureSettings, userEntitlements, enableAIHub, identity, isCopilotRoute, isBCAIS, isPinningEligible);
        case PreinstalledAppNames.OneDrive:
            return ((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.oneDriveMosAppGates) === null || _a === void 0 ? void 0 : _a.oneDriveMosAppEnabled) !== null && _b !== void 0 ? _b : false);
        case PreinstalledAppNames.Prepare:
            return enableAIHub && !isPrepareNativeModuleEnabled(featureGates);
        case PreinstalledAppNames.CatchUp:
            return enableAIHub && isCatchUpNativeDisabled(featureGates);
        case PreinstalledAppNames.Create:
            return enableAIHub && !isCreateNativeEnabled(featureGates);
        default:
            return false;
    }
};
const getPreinstalledApps = (capabilitiesConfig, featureGates, featureSettings, userEntitlements, summarizeActionText, enableAIHub, identity, isCopilotRoute, isBCAIS, isPinningEligible) => __awaiter(void 0, void 0, void 0, function* () {
    const appDefinitions = [];
    const candidateMOS3AppEntries = yield getCandidateMOS3AppEntries(summarizeActionText);
    candidateMOS3AppEntries.forEach((appEntry) => {
        if (shouldInstall(capabilitiesConfig, appEntry.appEnumName, featureGates, featureSettings, userEntitlements, enableAIHub, identity, isCopilotRoute, isBCAIS, isPinningEligible)) {
            const harmonyApp = getHarmonyAppDefinition(appEntry.definition);
            harmonyApp && appDefinitions.push(harmonyApp);
        }
    });
    return appDefinitions;
});
//# sourceMappingURL=fetchPreinstalledApps.js.map